// Copyright The Perses Authors
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import { ReactElement, useState } from 'react';
import { useVariableValues } from '@perses-dev/plugin-system';
import { Dialog } from '@perses-dev/components';
import { Button } from '@mui/material';
import { usePanelGroupEditor } from '../../context';
import { PanelGroupEditorForm, panelGroupEditorFormId, PanelGroupEditorFormProps } from './PanelGroupEditorForm';

/**
 * A dialog for adding or editing a Panel Group. Open and initial state is controlled by the DashboardStore.
 */
export function PanelGroupDialog(): ReactElement {
  const panelGroupEditor = usePanelGroupEditor();
  const variables = useVariableValues();

  // When the user clicks close, start closing but don't call the store yet to keep values stable during animtation
  const [isClosing, setIsClosing] = useState(false);
  const handleClose = (): void => setIsClosing(true);

  // Don't call close on the store until the Dialog has completely transitioned out
  const handleExited = (): void => {
    panelGroupEditor?.close();
    setIsClosing(false);
  };

  // Dialog is open if we have a model and we're not transitioning out
  const isOpen = panelGroupEditor !== undefined && isClosing === false;

  const handleSubmit: PanelGroupEditorFormProps['onSubmit'] = (values) => {
    // This shouldn't happen since we don't render the submit button until we have a model, but check to make TS happy
    if (panelGroupEditor === undefined) {
      throw new Error('Cannot apply changes');
    }
    panelGroupEditor.applyChanges(values);
    handleClose();
  };

  return (
    <Dialog open={isOpen} TransitionProps={{ onExited: handleExited }}>
      {panelGroupEditor !== undefined && (
        <>
          <Dialog.Header>{panelGroupEditor.mode} Panel Group</Dialog.Header>
          <Dialog.Content dividers sx={{ width: '500px' }}>
            <PanelGroupEditorForm
              initialValues={panelGroupEditor.initialValues}
              variables={Object.keys(variables)}
              onSubmit={handleSubmit}
            />
          </Dialog.Content>
          <Dialog.Actions>
            <Button variant="contained" type="submit" form={panelGroupEditorFormId}>
              {panelGroupEditor.mode === 'Edit' ? 'Apply' : 'Add'}
            </Button>
            <Button variant="outlined" color="secondary" onClick={panelGroupEditor.close}>
              Cancel
            </Button>
          </Dialog.Actions>
        </>
      )}
    </Dialog>
  );
}
